import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { createItem, api } from "@/lib/api";
import { Loader2, CalendarIcon, ImagePlus, X } from "lucide-react";
import { format } from "date-fns";
import { analytics } from "@/lib/analytics";
import { searchCities, regionLabel } from "@/lib/ncRegions";

/**
 * City field: suggests known NC cities but accepts anything typed.
 * NC has ~550 municipalities — we suggest the ~55 that matter and let
 * the long tail through as free text rather than blocking anyone.
 */
function CityField({ id, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const suggestions = value !== undefined ? searchCities(value || "") : [];
  const show = open && suggestions.length > 0;

  const pick = (city) => { onChange(city); setOpen(false); setActive(0); };

  const onKeyDown = (e) => {
    if (!show) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => (i + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative">
      <Input
        id={id}
        value={value || ""}
        placeholder={placeholder || "Start typing your city…"}
        autoComplete="off"
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
      />
      {show && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-xl border border-border bg-popover shadow-md py-1">
          {suggestions.map((city, i) => (
            <li key={city}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(city)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                  i === active ? "bg-muted text-foreground" : "text-foreground/80"
                }`}
              >
                <span>{city}</span>
                <span className="text-[11px] text-muted-foreground shrink-0">{regionLabel(city)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {value && !show && (
        <p className="mt-1 text-[11px] text-muted-foreground">{regionLabel(value)}</p>
      )}
    </div>
  );
}

const BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// A select whose options include "Other" gets a companion free-text field,
// so "Other" resolves to a real city name instead of a useless "Other".
const hasOther = (f) => f.type === "select" && Array.isArray(f.options) && f.options.includes("Other");
const otherKey = (name) => `${name}__other`;

function initialState(fields) {
  const s = {};
  fields.forEach(f => {
    if (f.type === "checkbox") s[f.name] = false;
    else if (f.type === "images") s[f.name] = [];
    else s[f.name] = f.default ?? "";
    if (hasOther(f)) s[otherKey(f.name)] = "";
  });
  return s;
}

function ImageUploadField({ label, name, value, onChange, multiple = false }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef();

  const upload = async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(BASE + data.url);
      }
      analytics.uploadPhoto(uploaded.length);
      if (multiple) {
        onChange([...value, ...uploaded]);
      } else {
        onChange(uploaded[0] || "");
      }
    } catch {
      analytics.uploadError();
      toast.error("Image upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => {
    if (multiple) onChange(value.filter((_, i) => i !== idx));
    else onChange("");
  };

  const previews = multiple ? value : (value ? [value] : []);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {previews.map((url, idx) => (
          <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden border border-border group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            {idx === 0 && multiple && (
              <span className="absolute bottom-0 left-0 right-0 text-[9px] text-center bg-primary text-white py-0.5">Cover</span>
            )}
            <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {(multiple || previews.length === 0) && (
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={uploading}
            className="w-20 h-20 rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary transition-colors text-xs"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImagePlus className="w-5 h-5" />{multiple ? "Add photo" : "Upload"}</>}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" multiple={multiple} className="hidden"
        onChange={e => e.target.files?.length && upload(Array.from(e.target.files))} />
      {multiple && previews.length > 0 && <p className="text-xs text-muted-foreground">First image is the cover photo shown on the listing card.</p>}
    </div>
  );
}

export function FormDialog({ trigger, title, description, fields, resource, onCreated, submitLabel = "Submit", testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(() => initialState(fields));
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !values[f.name] && f.type !== "checkbox" && f.type !== "images") {
        toast.error(`${f.label} is required`); return;
      }
      if (hasOther(f) && values[f.name] === "Other" && !values[otherKey(f.name)]?.trim()) {
        toast.error(`Please type your ${f.label.toLowerCase()}`); return;
      }
    }
    setLoading(true);
    try {
      const payload = { ...values };
      fields.forEach(f => {
        // Replace "Other" with what they actually typed, then drop the helper key
        if (hasOther(f)) {
          if (payload[f.name] === "Other" && payload[otherKey(f.name)]?.trim()) {
            payload[f.name] = payload[otherKey(f.name)].trim();
          }
          delete payload[otherKey(f.name)];
        }
        if (f.type === "number" && payload[f.name] !== "") payload[f.name] = Number(payload[f.name]);
        if (f.type === "images") {
          // Send cover image as photo, rest as gallery JSON
          const imgs = payload[f.name] || [];
          payload.photo = imgs[0] || null;
          payload.gallery = JSON.stringify(imgs.slice(1));
          delete payload[f.name];
        }
      });
      const created = await createItem(resource, payload);
      analytics.submitForm(resource);
      toast.success("Submitted — now in review 🎉", {
        description: "Every submission gets a quick human check before it goes live. You'll see yours marked \"In review\" on the board until then.",
      });
      setValues(initialState(fields));
      setOpen(false);
      onCreated?.(created);
    } catch {
      analytics.formError(resource);
      toast.error("Something went wrong", { description: "Please try again." });
    }
    finally { setLoading(false); }
  };

  const handleOpenChange = (next) => {
    if (next) analytics.openForm(resource);
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {fields.map(f => (
            <div key={f.name} className="space-y-1.5">
              {f.type === "images" ? (
                <ImageUploadField label={f.label} name={f.name} value={values[f.name]} onChange={v => set(f.name, v)} multiple={f.multiple !== false} />
              ) : f.type === "image" ? (
                <ImageUploadField label={f.label} name={f.name} value={values[f.name]} onChange={v => set(f.name, v)} multiple={false} />
              ) : (
                <>
                  {f.type !== "checkbox" && <Label htmlFor={f.name}>{f.label}{f.required && <span className="text-primary"> *</span>}</Label>}
                  {f.type === "textarea" && <Textarea id={f.name} placeholder={f.placeholder} value={values[f.name]} onChange={e => set(f.name, e.target.value)} rows={f.rows || 4} className="resize-none" />}
                  {f.type === "city" && (
                    <CityField id={f.name} value={values[f.name]} onChange={v => set(f.name, v)} placeholder={f.placeholder} />
                  )}
                  {(f.type === "text" || f.type === "number") && <Input id={f.name} type={f.type === "number" ? "number" : "text"} placeholder={f.placeholder} value={values[f.name]} onChange={e => set(f.name, e.target.value)} />}
                  {f.type === "select" && (
                    <>
                      <Select value={values[f.name]} onValueChange={v => set(f.name, v)}>
                        <SelectTrigger id={f.name}><SelectValue placeholder={f.placeholder || "Select…"} /></SelectTrigger>
                        <SelectContent>{f.options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                      </Select>
                      {hasOther(f) && values[f.name] === "Other" && (
                        <Input
                          autoFocus
                          className="mt-2"
                          placeholder={f.otherPlaceholder || `Which ${f.label.toLowerCase()}?`}
                          value={values[otherKey(f.name)] || ""}
                          onChange={e => set(otherKey(f.name), e.target.value)}
                        />
                      )}
                    </>
                  )}
                  {f.type === "date" && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                          {values[f.name] ? format(new Date(values[f.name] + "T00:00:00"), "EEE, MMM d, yyyy") : <span className="text-muted-foreground">{f.placeholder || "Pick a date"}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={values[f.name] ? new Date(values[f.name] + "T00:00:00") : undefined} onSelect={d => d && set(f.name, format(d, "yyyy-MM-dd"))} disabled={d => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                      </PopoverContent>
                    </Popover>
                  )}
                  {f.type === "checkbox" && (
                    <label className="flex items-center gap-2.5 cursor-pointer pt-1">
                      <Checkbox id={f.name} checked={values[f.name]} onCheckedChange={v => set(f.name, !!v)} />
                      <span className="text-sm">{f.label}</span>
                    </label>
                  )}
                </>
              )}
            </div>
          ))}
          <DialogFooter className="pt-2">
            <Button type="submit" disabled={loading} className="w-full sm:w-auto rounded-full px-8">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
